import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { isNavigationPathActive, type NavigationGroup } from './navigation'

interface GroupedNavigationProps {
  groups: NavigationGroup[]
  pathname: string
  onNavigate: () => void
}

export function GroupedNavigation({
  groups,
  pathname,
  onNavigate,
}: GroupedNavigationProps) {
  return (
    <div className="mt-2 ml-4 space-y-4 border-l border-border-default pb-2 pl-2">
      {groups.map((group) => {
        const GroupIcon = group.icon

        return (
          <section key={group.id} aria-label={group.label}>
            <div
              className={clsx(
                'mb-1 flex items-center gap-1.5 overflow-hidden px-2 text-[10px] font-bold tracking-[0.18em] whitespace-nowrap uppercase',
                group.disabled ? 'text-muted' : 'text-secondary',
              )}
            >
              <GroupIcon size={12} className="shrink-0" aria-hidden="true" />
              <span className="truncate">
                {group.label}
                {group.labelSuffix && (
                  <span className="ml-1 font-normal tracking-normal normal-case">
                    {group.labelSuffix}
                  </span>
                )}
              </span>
              {group.badge && (
                <span className="ml-1 shrink-0 rounded bg-raised px-1 py-0.5 text-[9px] font-medium tracking-normal text-muted normal-case">
                  {group.badge}
                </span>
              )}
            </div>

            <div className="space-y-0.5">
              {group.items.map((item) => {
                const target =
                  !group.disabled && item.migrated ? item.to : undefined
                const active = isNavigationPathActive(pathname, target)
                const itemClassName = clsx(
                  'flex min-h-8 w-full min-w-0 items-center rounded-md border-l-2 px-3 py-1.5 text-left text-xs no-underline transition-colors duration-200 motion-reduce:transition-none',
                  !target
                    ? 'cursor-not-allowed border-transparent text-muted'
                    : active
                      ? 'border-accent bg-accent-faint font-medium text-accent'
                      : 'border-transparent text-secondary hover:bg-inset hover:text-primary',
                )
                const content = <span className="truncate">{item.label}</span>

                if (!target) {
                  return (
                    <button
                      key={item.id}
                      type="button"
                      disabled
                      title={`${item.label} has not been migrated yet`}
                      className={itemClassName}
                    >
                      {content}
                    </button>
                  )
                }

                return (
                  <Link
                    key={item.id}
                    to={target}
                    aria-current={active ? 'page' : undefined}
                    className={itemClassName}
                    onClick={onNavigate}
                  >
                    {content}
                  </Link>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
